import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { ScrollArea } from './ui/scroll-area';
import {
  Sparkles,
  MessageSquare,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  Brain,
  Target,
  Users,
  Calendar,
  Server,
  TrendingUp,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  extractedData?: any;
  confidence?: number;
}

interface ExtractedField {
  field: string;
  value: any;
  confidence: 'high' | 'medium' | 'low';
  source: string;
}

interface BDMAssistantProps {
  accessToken: string;
  initialNotes?: string;
  onDataExtracted: (data: any) => void;
  onComplete: () => void;
}

export function BDMAssistant({
  accessToken,
  initialNotes = '',
  onDataExtracted,
  onComplete,
}: BDMAssistantProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [extractedFields, setExtractedFields] = useState<ExtractedField[]>([]);
  const [showExtractedData, setShowExtractedData] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initialize with welcome message
    if (initialNotes) {
      handleInitialAnalysis();
    } else {
      setMessages([
        {
          id: '1',
          role: 'assistant',
          content: "This is the BDM Assistant. It extracts key information from your discovery notes. You can paste your notes, or answer questions to gather the information needed.",
          timestamp: new Date(),
        },
      ]);
    }
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleInitialAnalysis = async () => {
    setLoading(true);

    const systemMessage: Message = {
      id: Date.now().toString(),
      role: 'system',
      content: 'Analyzing your discovery notes...',
      timestamp: new Date(),
    };

    setMessages([systemMessage]);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/bdm/conversational-extract`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            notes: initialNotes,
            conversationHistory: [],
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        setExtractedData(data.extractedData);
        setExtractedFields(data.fields || []);
        onDataExtracted(data.extractedData);

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          extractedData: data.extractedData,
          confidence: data.overallConfidence,
        };

        setMessages([systemMessage, assistantMessage]);

        // If there are clarifying questions, add them
        if (data.clarifyingQuestions && data.clarifyingQuestions.length > 0) {
          const questionMessage: Message = {
            id: (Date.now() + 2).toString(),
            role: 'assistant',
            content: "I have a few questions to fill in some gaps:\n\n" + 
              data.clarifyingQuestions.map((q: string, i: number) => `${i + 1}. ${q}`).join('\n'),
            timestamp: new Date(),
          };
          setMessages(prev => [...prev, questionMessage]);
        }
      } else {
        throw new Error('Failed to analyze notes');
      }
    } catch (err) {
      console.error('Error analyzing notes:', err);
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: "I had trouble analyzing those notes. Could you tell me about the client in your own words? What are they hoping to achieve?",
        timestamp: new Date(),
      };
      setMessages([systemMessage, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/bdm/conversational-extract`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            notes: initialNotes,
            userMessage: input,
            conversationHistory: messages.map(m => ({
              role: m.role,
              content: m.content,
            })),
            currentData: extractedData,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        setExtractedData(data.extractedData);
        setExtractedFields(data.fields || []);
        onDataExtracted(data.extractedData);

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          extractedData: data.extractedData,
          confidence: data.overallConfidence,
        };

        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error('Failed to process message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: "I'm having trouble processing that. Could you rephrase or provide more details?",
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getFieldIcon = (field: string) => {
    if (field.includes('goal') || field.includes('objective')) return Target;
    if (field.includes('stakeholder') || field.includes('contact')) return Users;
    if (field.includes('timeline') || field.includes('date')) return Calendar;
    if (field.includes('tech') || field.includes('platform')) return Server;
    if (field.includes('pain') || field.includes('challenge')) return TrendingUp;
    return Brain;
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-primary" />
                BDM Discovery Assistant
              </CardTitle>
              <CardDescription>
                AI-powered extraction of client information from your discovery notes
              </CardDescription>
            </div>
            {extractedData && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExtractedData(!showExtractedData)}
              >
                {showExtractedData ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-1" />
                    Hide Data
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Show Data
                  </>
                )}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Extracted Data Summary */}
          {extractedData && showExtractedData && (
            <div className="p-4 bg-muted/50 rounded-lg border space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  Extracted Information
                </h4>
                {extractedFields.length > 0 && (
                  <Badge variant="secondary">
                    {extractedFields.length} fields captured
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {extractedFields.map((field, idx) => {
                  const Icon = getFieldIcon(field.field);
                  return (
                    <div key={idx} className="p-3 bg-background rounded border">
                      <div className="flex items-start gap-2">
                        <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                              {field.field}
                            </p>
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${getConfidenceColor(field.confidence)}`}
                            >
                              {field.confidence}
                            </Badge>
                          </div>
                          <p className="text-sm break-words">
                            {Array.isArray(field.value) 
                              ? field.value.join(', ') 
                              : typeof field.value === 'object'
                              ? JSON.stringify(field.value)
                              : field.value}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Chat Interface */}
          <div className="border rounded-lg overflow-hidden">
            <ScrollArea className="h-[400px] p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : message.role === 'system'
                          ? 'bg-muted/50 text-muted-foreground italic'
                          : 'bg-muted'
                      }`}
                    >
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2">
                          <MessageSquare className="h-3 w-3" />
                          <span className="text-xs font-semibold">BDM Assistant</span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      {message.confidence && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs opacity-70">
                            Confidence: {Math.round(message.confidence * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-lg p-3">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Input Area */}
            <div className="border-t p-3 bg-background">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask a question or provide more details..."
                  disabled={loading}
                  className="flex-1"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!input.trim() || loading}
                  size="sm"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-muted-foreground">
              Tip: Ask about goals, timelines, stakeholders, or technical requirements
            </div>
            {extractedData && (
              <Button onClick={onComplete} size="sm">
                <CheckCircle className="h-4 w-4 mr-2" />
                Use This Data
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}